<template>
  <div class="match-list">
    <!-- Skeleton loading -->
    <template v-if="loading && matches.length === 0">
      <div class="match-skeleton" v-for="i in 6" :key="`sk-${i}`">
        <div class="skeleton match-skeleton__league"></div>
        <div class="match-skeleton__body">
          <div class="skeleton match-skeleton__star"></div>
          <div class="skeleton match-skeleton__team"></div>
          <div class="skeleton match-skeleton__vs"></div>
          <div class="skeleton match-skeleton__team"></div>
        </div>
        <div class="match-skeleton__odds">
          <div class="skeleton match-skeleton__odds-val" v-for="j in 4" :key="j"></div>
        </div>
      </div>
    </template>

    <!-- Match cards -->
    <template v-else-if="matches.length">
      <transition-group name="fade" tag="div">
        <match-card
          v-for="(match, index) in matches"
          :key="match.analysis_id || match.id"
          :match="match"
          :is-first="index === 0"
          :popular-mode="popularMode"
          :show-date="showDate"
        />
      </transition-group>

      <!-- Load more sentinel -->
      <div ref="sentinel" class="sentinel"></div>

      <!-- Loading more indicator -->
      <div class="load-more" v-if="loadingMore">
        <div class="load-more__spinner"></div>
        <span>載入更多...</span>
      </div>
    </template>

    <!-- Empty state -->
    <div class="match-list__empty" v-else-if="!loading">
      <div class="empty-illustration">⚽</div>
      <p class="empty-title">{{ emptyTitle }}</p>
      <p class="empty-sub">{{ emptySub }}</p>
    </div>
  </div>
</template>

<script>
import MatchCard from './MatchCard.vue'

export default {
  name: 'MatchList',
  components: { MatchCard },
  props: {
    matches: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingMore: {
      type: Boolean,
      default: false
    },
    hasMore: {
      type: Boolean,
      default: false
    },
    emptyTitle: {
      type: String,
      default: '今日暫無賽程'
    },
    emptySub: {
      type: String,
      default: '請選擇其他日期查詢'
    },
    popularMode: {
      type: Boolean,
      default: false
    },
    showDate: {
      type: Boolean,
      default: false
    }
  },
  mounted() {
    this.setupObserver()
  },
  beforeDestroy() {
    this.destroyObserver()
  },
  watch: {
    hasMore(val) {
      if (val) {
        this.$nextTick(() => this.setupObserver())
      }
    }
  },
  methods: {
    setupObserver() {
      this.destroyObserver()
      if (!this.$refs.sentinel) return
      this.observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && this.hasMore && !this.loadingMore && !this.loading) {
          this.$emit('load-more')
        }
      }, { threshold: 0.1, rootMargin: '100px' })
      this.observer.observe(this.$refs.sentinel)
    },
    destroyObserver() {
      if (this.observer) {
        this.observer.disconnect()
        this.observer = null
      }
    }
  }
}
</script>

<style scoped>
.match-list {
  min-height: 200px;
}

/* Skeleton */
.match-skeleton {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
}
.match-skeleton__league {
  height: 12px;
  width: 120px;
  margin-bottom: 8px;
}
.match-skeleton__body {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.match-skeleton__star {
  width: 18px;
  height: 18px;
  border-radius: 50%;
}
.match-skeleton__team {
  flex: 1;
  height: 16px;
  border-radius: var(--radius-sm);
}
.match-skeleton__vs {
  width: 40px;
  height: 16px;
  border-radius: var(--radius-sm);
}
.match-skeleton__odds {
  display: flex;
  gap: 8px;
  padding-left: 30px;
}
.match-skeleton__odds-val {
  width: 36px;
  height: 11px;
  border-radius: var(--radius-sm);
}

/* Sentinel for infinite scroll */
.sentinel {
  height: 1px;
  visibility: hidden;
}

/* Load more */
.load-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px;
  color: var(--text-muted);
  font-size: 13px;
}
.load-more__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-strong);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Empty state */
.match-list__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  color: var(--text-muted);
}
.empty-illustration {
  font-size: 56px;
  margin-bottom: 16px;
  filter: grayscale(0.5);
}
.empty-title {
  font-size: 16px;
  color: var(--text-secondary);
  font-weight: 600;
  margin-bottom: 6px;
}
.empty-sub {
  font-size: 13px;
  color: var(--text-muted);
}
</style>
